import { ReviewedStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/booking_submission.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { ScheduleQueries } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getUser, hasSchemaACL } from '@frontend/auth-helpers';
import { ActionsUI } from '@frontend/contact-actions';
import { ThreadContextAndOptions } from '@frontend/contact-actions-send-message';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { formatDate, useAlert, useModalControl, usePopoverMenu } from '@frontend/design-system';
import { useScheduleSettingsSmallMode } from '../../../../../../hooks/settings/use-schedule-settings-small-mode';
import { useScheduleActionsContext } from '../../ScheduleActionsContext';
import { useScheduleRequestContext } from '../Context/ScheduleRequestContext';

export const useScheduleRequestItemHeaderController = ({
  requestVersion,
  locationId,
  scheduleRequestId,
  appointmentTime,
  isWidgetListItem,
  markNotificationsAsRead,
}: {
  requestVersion: string;
  scheduleRequestId: string;
  locationId: string;
  appointmentTime: string;
  isWidgetListItem?: boolean;
  markNotificationsAsRead: (value: string) => void;
}) => {
  const { t } = useTranslation('schedule-requests');
  const alert = useAlert();
  const user = getUser();
  const userId = user?.userID ?? '';
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { isSmallMode } = useScheduleSettingsSmallMode();
  const { getLocationName } = useAppScopeStore();
  const locationName = getLocationName(locationId);
  const shouldUseV3 = requestVersion !== 'v2' && isScheduleV3FlagEnabled;
  const appointmentTimeLabel =
    shouldUseV3 && !isWidgetListItem && !isSmallMode
      ? t('Requested on {{requestTime}}', { requestTime: formatDate(appointmentTime, 'MMM D YYYY, h:mm A') })
      : formatDate(appointmentTime, 'MMM D YYYY, h:mm A');

  const deleteScheduleRequestModalProps = useModalControl();
  const rejectScheduleRequestModalControl = useModalControl();

  const {
    getMenuProps,
    getTriggerProps,
    close: closePopOverMenu,
  } = usePopoverMenu({
    placement: 'bottom-start',
  });

  const { refetchScheduleRequestList } = useScheduleRequestContext();
  const { closeModal, refetchAppointmentRequestCount } = useScheduleActionsContext();

  const { onClick: messageOnClick } = ActionsUI.actions.useMessageAction();
  const { featureFlags } = useAppFlagStore();
  const canSendMessages = featureFlags[locationId]?.get('nwx:messaging-integrated-messaging-fe-acl-enforcement')?.value
    ? !!hasSchemaACL(locationId, Permission.SMS_INBOX_MANAGE)
    : true;

  const { mutateAsync: deleteScheduleRequest, isLoading: isLoadingDeleteScheduleRequest } =
    ScheduleQueries.useDeleteScheduleRequest();
  const { mutateAsync: deleteScheduleRequestV3, isLoading: isLoadingDeleteScheduleRequestV3 } =
    SchedulerV3.Mutations.useDeleteBookingSubmissionMutation();
  const { mutateAsync: updateBookingSubmissionV3, isLoading: isUpdatingBookingSubmissionV3 } =
    SchedulerV3.Mutations.useUpdateBookingSubmissionMutation();

  const handleDeleteScheduleRequest = async () => {
    try {
      if (shouldUseV3) {
        await deleteScheduleRequestV3({ submissionIds: [scheduleRequestId] });
      } else {
        await deleteScheduleRequest({ id: scheduleRequestId, locationId });
        refetchAppointmentRequestCount();
        refetchScheduleRequestList();
      }
      deleteScheduleRequestModalProps.closeModal();
      alert.success(t('Appointment request deleted successfully'));
    } catch (error) {
      alert.error(t('Failed to delete appointment request'));
      throw error;
    }
  };

  const handleRejectScheduleRequest = async () => {
    try {
      await updateBookingSubmissionV3({
        submissionId: scheduleRequestId,
        reviewedBy: userId,
        reviewedStatus: ReviewedStatus.REJECTED,
      });
      rejectScheduleRequestModalControl.closeModal();
      alert.success(t('Appointment request rejected successfully'));
    } catch (error) {
      alert.error(t('Failed to reject appointment request'));
      throw error;
    }
  };

  const handleDismissScheduleRequest = () => {
    // TODO: Implement dismiss functionality for V3 when API is ready
  };

  const handleDoneScheduleRequest = () => {
    // TODO: Implement mark as done functionality for V3 when API is ready
  };

  const markAndClose = () => {
    markNotificationsAsRead(scheduleRequestId);
    closePopOverMenu();
  };

  const handleReviewRequestMenuItemClick = (onReviewRequest: () => void) => {
    onReviewRequest();
    markAndClose();
  };

  const handleDeleteMenuItemClick = () => {
    deleteScheduleRequestModalProps.openModal();
    markAndClose();
  };

  const handleRejectRequestMenuItemClick = () => {
    rejectScheduleRequestModalControl.openModal();
    markAndClose();
  };

  const handleDismissMenuItemClick = () => {
    // TODO: Implement dismiss action click handler
    markAndClose();
  };

  const handleDoneMenuItemClick = () => {
    // TODO: Implement done action click handler
    markAndClose();
  };

  const handleMessageMenuItemClick = async (context: ThreadContextAndOptions) => {
    await messageOnClick(context);
    closeModal();
    markAndClose();
  };

  const handleCallMenuItemClick = async () => {
    // TODO: Implement call action click handler
    markAndClose();
  };

  return {
    shouldUseV3,
    locationName,
    appointmentTimeLabel,
    canSendMessages,
    isDeleting: isLoadingDeleteScheduleRequest || isLoadingDeleteScheduleRequestV3,
    isUpdating: isUpdatingBookingSubmissionV3,
    deleteScheduleRequestModalProps,
    rejectScheduleRequestModalControl,
    getMenuProps,
    getTriggerProps,
    handleDeleteScheduleRequest,
    handleRejectScheduleRequest,
    handleDismissScheduleRequest,
    handleDoneScheduleRequest,
    handleReviewRequestMenuItemClick,
    handleRejectRequestMenuItemClick,
    handleDismissMenuItemClick,
    handleDoneMenuItemClick,
    handleCallMenuItemClick,
    handleDeleteMenuItemClick,
    handleMessageMenuItemClick,
  };
};
